import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit, Trash2, Printer, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { useAuth } from '../AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const fmtR = (n) => `${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { if (!s) return '—'; try { const d = new Date(s); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getFullYear()).slice(-2)}`; } catch { return s; } };

// Indian-numbering number→words. Returns e.g. "Seven Lakh Forty Four Thousand Rupees Only"
const _ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const _tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const _hundreds = (n) => {
  let s = '';
  if (n >= 100) { s += _ones[Math.floor(n / 100)] + ' Hundred '; n %= 100; }
  if (n >= 20) { s += _tens[Math.floor(n / 10)] + ' '; n %= 10; }
  if (n > 0) s += _ones[n] + ' ';
  return s.trim();
};
const numberToIndianWords = (num) => {
  num = Math.round(Number(num) * 100) / 100; // 2-dp
  if (!isFinite(num) || num === 0) return 'Zero Rupees Only';
  const negative = num < 0; num = Math.abs(num);
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  const segs = [];
  const cr = Math.floor(rupees / 10000000); const rem1 = rupees % 10000000;
  const lk = Math.floor(rem1 / 100000);     const rem2 = rem1 % 100000;
  const th = Math.floor(rem2 / 1000);       const rem3 = rem2 % 1000;
  if (cr > 0) segs.push(_hundreds(cr) + ' Crore');
  if (lk > 0) segs.push(_hundreds(lk) + ' Lakh');
  if (th > 0) segs.push(_hundreds(th) + ' Thousand');
  if (rem3 > 0) segs.push(_hundreds(rem3));
  let out = segs.join(' ').trim();
  if (!out) out = 'Zero';
  out = (negative ? 'Minus ' : '') + out + ' Rupees';
  if (paise > 0) out += ' and ' + _hundreds(paise) + ' Paise';
  return out + ' Only';
};

const emptyForm = () => ({
  invoice_no: '',
  invoice_date: new Date().toISOString().split('T')[0],
  due_date: '',
  sender_name: 'WANGJING WOMEN AND GIRLS SOCIETY (WWAGS)',
  sender_address: 'NH-102, WWAGS BUILDING, WANGJING BAZAR, OPP. POST OFFICE, Thoubal, Manipur, 795148, India',
  sender_gstin: '',
  sender_email: 'wwagss@gmail.com',
  sender_website: 'www.wwags.org',
  bill_to_name: '',
  bill_to_address: '',
  bill_to_gstin: '',
  bill_to_email: '',
  bill_to_phone: '',
  line_items: [{ description: '', price: 0, qty: 1, discount_pct: 0 }],
  gst_rate: 0,
  gst_type: 'none',
  advance_paid: 0,
  total_label: 'Total Due',   // 'Total Due' | 'Total' | 'Grand Total' | 'Net Total'
  bank_account_name: 'Wangjing Women and Girls Society',
  bank_account_number: '10678276383',
  bank_name: 'State Bank of India',
  ifsc: 'SBIN00005320',
  notes: '',
  status: 'draft',
});

const calcTotals = (inv) => {
  const items = (inv.line_items || []).map(r => {
    const gross = Number(r.price || 0) * Number(r.qty || 0);
    const disc = gross * (Number(r.discount_pct || 0) / 100);
    return { ...r, amount: gross - disc };
  });
  const netAmount = items.reduce((s, r) => s + r.amount, 0);
  const discountTotal = items.reduce((s, r) => s + (Number(r.price || 0) * Number(r.qty || 0) * (Number(r.discount_pct || 0) / 100)), 0);
  const gstRate = Number(inv.gst_rate || 0);
  const gstAmount = gstRate > 0 ? netAmount * gstRate / 100 : 0;
  const cgst = inv.gst_type === 'cgst_sgst' ? gstAmount / 2 : 0;
  const sgst = inv.gst_type === 'cgst_sgst' ? gstAmount / 2 : 0;
  const igst = inv.gst_type === 'igst' ? gstAmount : 0;
  const total = netAmount + gstAmount;
  const advance = Math.max(0, Number(inv.advance_paid || 0));
  const balance = Math.max(0, total - advance);
  return { items, netAmount, discountTotal, gstAmount, cgst, sgst, igst, total, advance, balance };
};

const Invoices = () => {
  const { isAdminOnly } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [previewInv, setPreviewInv] = useState(null);
  // Customers (Bill-to address book)
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('__new__');
  const [saveCustomer, setSaveCustomer] = useState(true);
  // Optional sections
  const [enableDiscount, setEnableDiscount] = useState(false);

  const fetchList = async () => {
    try {
      const [invRes, custRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/invoice-customers').catch(() => ({ data: [] })),
      ]);
      setList(invRes.data || []);
      setCustomers(custRes.data || []);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, []);

  const totals = useMemo(() => calcTotals(form), [form]);

  if (!isAdminOnly()) {
    return <div className="p-8"><p className="text-rose-600 text-sm">Only Admin can access Invoices.</p></div>;
  }

  const updateItem = (i, field, val) => {
    const items = [...(form.line_items || [])];
    items[i] = { ...items[i], [field]: val };
    setForm({ ...form, line_items: items });
  };
  const addItem = () => setForm({ ...form, line_items: [...(form.line_items || []), { description: '', price: 0, qty: 1, discount_pct: 0 }] });
  const removeItem = (i) => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.bill_to_name) { toast.error('Bill-to name is required'); return; }
    if (!(form.line_items || []).length) { toast.error('Add at least one line item'); return; }
    try {
      const payload = { ...form };
      // If discount toggle is off, strip per-line discounts
      if (!enableDiscount) {
        payload.line_items = (payload.line_items || []).map(r => ({ ...r, discount_pct: 0 }));
      }
      if (!payload.invoice_no) delete payload.invoice_no;

      // Save / update Bill-to as a re-usable customer record (only for new customers and only if the user opted in)
      if (saveCustomer && selectedCustomerId === '__new__') {
        try {
          const created = await api.post('/invoice-customers', {
            name: form.bill_to_name,
            address: form.bill_to_address,
            gstin: form.bill_to_gstin || null,
          });
          setCustomers(prev => [...prev, created.data]);
          setSelectedCustomerId(created.data.id);
        } catch {/* non-fatal */}
      } else if (saveCustomer && selectedCustomerId && selectedCustomerId !== '__new__') {
        // Keep saved customer fresh if user edited fields
        try {
          await api.put(`/invoice-customers/${selectedCustomerId}`, {
            name: form.bill_to_name,
            address: form.bill_to_address,
            gstin: form.bill_to_gstin || null,
          });
        } catch {/* non-fatal */}
      }

      if (editId) {
        await api.put(`/invoices/${editId}`, payload);
        toast.success('Invoice updated');
      } else {
        await api.post('/invoices', payload);
        toast.success('Invoice created');
      }
      setDialogOpen(false); setEditId(null); setForm(emptyForm());
      setSelectedCustomerId('__new__'); setSaveCustomer(true); setEnableDiscount(false);
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const pickCustomer = (id) => {
    setSelectedCustomerId(id);
    if (id === '__new__') {
      setForm(prev => ({ ...prev, bill_to_name: '', bill_to_address: '', bill_to_gstin: '' }));
      return;
    }
    const c = customers.find(x => x.id === id);
    if (c) {
      setForm(prev => ({ ...prev, bill_to_name: c.name, bill_to_address: c.address || '', bill_to_gstin: c.gstin || '' }));
    }
  };

  const openEdit = async (inv) => {
    setEditId(inv.id);
    // Preserve bank defaults if the loaded invoice has them blank (older drafts)
    const base = emptyForm();
    const merged = { ...base, ...inv };
    ['bank_account_name', 'bank_account_number', 'bank_name', 'ifsc'].forEach(k => {
      if (!merged[k]) merged[k] = base[k];
    });
    setForm({ ...merged, invoice_date: (inv.invoice_date || '').split('T')[0], due_date: (inv.due_date || '').split('T')[0] });
    // Try to match an existing saved customer by name + gstin
    const match = customers.find(c => c.name === inv.bill_to_name && (c.gstin || '') === (inv.bill_to_gstin || ''));
    setSelectedCustomerId(match ? match.id : '__new__');
    setSaveCustomer(false); // editing — don't re-save by default
    // Discount toggle: enable if any line has > 0
    setEnableDiscount((inv.line_items || []).some(r => Number(r.discount_pct || 0) > 0));
    setDialogOpen(true);
  };

  // Explicit "Save to address book" action — saves the current Bill-to as a customer
  // without waiting for the invoice form to be submitted. Useful when the user typed
  // a fresh vendor and wants it persisted immediately.
  const saveCustomerNow = async () => {
    if (!form.bill_to_name?.trim()) {
      toast.error('Enter the Bill-to name first');
      return;
    }
    try {
      const payload = {
        name: form.bill_to_name.trim(),
        address: form.bill_to_address || '',
        gstin: form.bill_to_gstin || '',
        phone: form.bill_to_phone || '',
        email: form.bill_to_email || '',
      };
      // If a saved customer is currently selected → update; otherwise create new
      if (selectedCustomerId && selectedCustomerId !== '__new__') {
        const updated = await api.put(`/invoice-customers/${selectedCustomerId}`, payload);
        setCustomers(prev => prev.map(c => c.id === selectedCustomerId ? updated.data : c));
        toast.success('Customer updated in address book');
      } else {
        const created = await api.post('/invoice-customers', payload);
        setCustomers(prev => [...prev, created.data]);
        setSelectedCustomerId(created.data.id);
        toast.success('Customer saved to address book');
      }
      setSaveCustomer(false); // already saved — no need to duplicate on form submit
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save customer');
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_no}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success('Deleted'); fetchList(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const print = (inv) => {
    const html = buildInvoiceHtml(inv);
    const w = window.open('', '_blank');
    if (w) { writePrintDoc(w.document, html); setTimeout(() => w.print(), 500); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full" data-testid="invoices-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileText className="w-6 h-6 text-emerald-700" /> Invoices</h1>
          <p className="text-sm text-slate-500">Generate professional GST-compliant bills in the WWAGS format.</p>
        </div>
        <Button onClick={() => { setEditId(null); setForm(emptyForm()); setDialogOpen(true); }} className="bg-emerald-700 hover:bg-emerald-800" data-testid="invoice-add">
          <Plus className="w-4 h-4 mr-1" /> New Invoice
        </Button>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Invoice No.</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Bill To</th>
                <th className="text-left px-3 py-2">GSTIN</th>
                <th className="text-right px-3 py-2">Total (INR)</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-center px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-slate-500 py-8">No invoices yet — click &ldquo;New Invoice&rdquo; to create one.</td></tr>
              ) : list.map(inv => {
                const t = calcTotals(inv);
                return (
                  <tr key={inv.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono">{inv.invoice_no}</td>
                    <td className="px-3 py-2">{fmtDate(inv.invoice_date)}</td>
                    <td className="px-3 py-2"><div className="font-medium">{inv.bill_to_name}</div></td>
                    <td className="px-3 py-2 font-mono text-xs">{inv.bill_to_gstin || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold">Rs. {fmtR(t.total)}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-800' : inv.status === 'sent' ? 'bg-blue-100 text-blue-800' : inv.status === 'cancelled' ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-700'}`}>{inv.status}</span></td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => setPreviewInv(inv)} className="p-1 text-slate-600 hover:bg-slate-100 rounded" title="Preview"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => print(inv)} className="p-1 text-emerald-700 hover:bg-emerald-50 rounded" title="Print"><Printer className="w-4 h-4" /></button>
                      <button onClick={() => openEdit(inv)} className="p-1 text-blue-700 hover:bg-blue-50 rounded" title="Edit"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => remove(inv)} className="p-1 text-rose-700 hover:bg-rose-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditId(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? 'Edit Invoice' : 'New Invoice'}</DialogTitle></DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
            <div><Label className="text-xs">Invoice No.</Label><Input value={form.invoice_no} onChange={e => setForm({ ...form, invoice_no: e.target.value })} placeholder="Auto-generated" /></div>
            <div><Label className="text-xs">Invoice Date *</Label><Input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} /></div>
            <div><Label className="text-xs">Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">From (Sender)</p>
              <Input value={form.sender_name} onChange={e => setForm({ ...form, sender_name: e.target.value })} placeholder="Sender name" />
              <textarea rows={3} value={form.sender_address} onChange={e => setForm({ ...form, sender_address: e.target.value })} className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" placeholder="Sender address" />
              <Input value={form.sender_gstin || ''} onChange={e => setForm({ ...form, sender_gstin: e.target.value })} placeholder="Sender GSTIN (optional)" />
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 space-y-2">
              <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Bill To *</p>
              {/* Saved customer picker — shows full details (name · address · GSTIN · email · phone) */}
              <div>
                <Label className="text-[10px] text-emerald-700">Pick a saved vendor / customer</Label>
                <Select value={selectedCustomerId} onValueChange={pickCustomer}>
                  <SelectTrigger className="h-auto min-h-[40px] text-xs py-2" data-testid="bill-to-customer-picker"><SelectValue placeholder="-- Choose --" /></SelectTrigger>
                  <SelectContent className="max-h-96">
                    <SelectItem value="__new__"><span className="font-semibold text-emerald-700">+ New vendor (enter below)</span></SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id} className="py-2">
                        <div className="flex flex-col gap-0.5 text-left leading-tight max-w-md">
                          <span className="font-semibold text-slate-900">{c.name}</span>
                          {c.address && <span className="text-[10px] text-slate-500 line-clamp-2">{c.address}</span>}
                          <span className="text-[10px] font-mono text-slate-600">
                            {c.gstin ? `GSTIN: ${c.gstin}` : 'GSTIN: —'}
                            {c.phone ? ` · ☎ ${c.phone}` : ''}
                            {c.email ? ` · ✉ ${c.email}` : ''}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input value={form.bill_to_name} onChange={e => setForm({ ...form, bill_to_name: e.target.value })} placeholder="Vendor / recipient name" />
              <textarea rows={3} value={form.bill_to_address} onChange={e => setForm({ ...form, bill_to_address: e.target.value })} className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" placeholder="Address" />
              <Input value={form.bill_to_gstin || ''} onChange={e => setForm({ ...form, bill_to_gstin: e.target.value })} placeholder="GSTIN (if any)" />
              <div className="grid grid-cols-2 gap-2">
                <Input value={form.bill_to_phone || ''} onChange={e => setForm({ ...form, bill_to_phone: e.target.value })} placeholder="Phone" />
                <Input value={form.bill_to_email || ''} onChange={e => setForm({ ...form, bill_to_email: e.target.value })} placeholder="Email" />
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <label className="flex items-center gap-2 text-[11px] text-emerald-800 cursor-pointer flex-1">
                  <input type="checkbox" checked={saveCustomer} onChange={e => setSaveCustomer(e.target.checked)} data-testid="save-customer-toggle" />
                  {selectedCustomerId === '__new__'
                    ? 'Save this Bill-to for future use'
                    : 'Update saved customer with these details'}
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-[11px] h-7 border-emerald-700 text-emerald-700 hover:bg-emerald-100 whitespace-nowrap"
                  onClick={saveCustomerNow}
                  data-testid="save-customer-now-btn"
                >
                  {selectedCustomerId === '__new__' ? 'Save now' : 'Update now'}
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-4 border border-slate-200 rounded">
            <div className="flex justify-between items-center bg-slate-100 px-3 py-2">
              <div className="flex items-center gap-4">
                <p className="text-xs font-bold text-slate-700">Line Items</p>
                <label className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={enableDiscount} onChange={e => setEnableDiscount(e.target.checked)} data-testid="discount-toggle" />
                  Apply line-level discount
                </label>
              </div>
              <Button size="sm" variant="outline" onClick={addItem}><Plus className="w-3 h-3 mr-1" />Item</Button>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase text-slate-500 bg-white">
                <tr>
                  <th className="text-left px-2 py-1.5">Description</th>
                  <th className="text-right px-2 py-1.5 w-28">Price</th>
                  <th className="text-right px-2 py-1.5 w-20">Qty</th>
                  {enableDiscount && <th className="text-right px-2 py-1.5 w-24">Discount %</th>}
                  <th className="text-right px-2 py-1.5 w-32">Amount</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(form.line_items || []).map((r, i) => {
                  const gross = Number(r.price || 0) * Number(r.qty || 0);
                  const disc = enableDiscount ? gross * (Number(r.discount_pct || 0) / 100) : 0;
                  const amt = gross - disc;
                  return (
                    <tr key={`li-${i}`} className={i % 2 ? 'bg-slate-50' : ''}>
                      <td className="px-2 py-1"><Input value={r.description} onChange={e => updateItem(i, 'description', e.target.value)} placeholder="Item description" /></td>
                      <td className="px-2 py-1"><Input type="number" value={r.price} onChange={e => updateItem(i, 'price', Number(e.target.value || 0))} className="text-right" /></td>
                      <td className="px-2 py-1"><Input type="number" value={r.qty} onChange={e => updateItem(i, 'qty', Number(e.target.value || 0))} className="text-right" /></td>
                      {enableDiscount && <td className="px-2 py-1"><Input type="number" value={r.discount_pct} onChange={e => updateItem(i, 'discount_pct', Number(e.target.value || 0))} className="text-right" /></td>}
                      <td className="px-2 py-1 text-right font-semibold">Rs. {fmtR(amt)}</td>
                      <td className="px-1"><button onClick={() => removeItem(i)} className="text-rose-600 hover:bg-rose-50 p-1 rounded"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <div className="md:col-span-2 bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">Bank Details (for payment)</p>
              <div className="grid grid-cols-2 gap-2">
                <Input value={form.bank_account_name} onChange={e => setForm({ ...form, bank_account_name: e.target.value })} placeholder="Account name" />
                <Input value={form.bank_account_number} onChange={e => setForm({ ...form, bank_account_number: e.target.value })} placeholder="Account number" />
                <Input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} placeholder="Bank name" />
                <Input value={form.ifsc} onChange={e => setForm({ ...form, ifsc: e.target.value })} placeholder="IFSC" />
              </div>
              <textarea rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full text-sm rounded-md border border-slate-300 px-3 py-2" placeholder="Notes (optional)" />
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 space-y-2">
              <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Tax / GST</p>
              <Select value={form.gst_type || 'none'} onValueChange={(v) => setForm({ ...form, gst_type: v, gst_rate: v === 'none' ? 0 : (form.gst_rate || 18) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No GST</SelectItem>
                  <SelectItem value="cgst_sgst">CGST + SGST (intra-state)</SelectItem>
                  <SelectItem value="igst">IGST (inter-state)</SelectItem>
                </SelectContent>
              </Select>
              {form.gst_type !== 'none' && (
                <div>
                  <Label className="text-xs">GST Rate (%)</Label>
                  <Input type="number" value={form.gst_rate} onChange={e => setForm({ ...form, gst_rate: Number(e.target.value || 0) })} />
                </div>
              )}
              <div>
                <Label className="text-xs">Advance Paid (Rs.)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.advance_paid}
                  onChange={e => setForm({ ...form, advance_paid: Number(e.target.value || 0) })}
                  placeholder="0.00"
                  data-testid="advance-paid-input"
                />
                <p className="text-[10px] text-emerald-700 mt-1">Amount already received — will be deducted from total to compute balance due.</p>
              </div>
              <div>
                <Label className="text-xs">Total Row Label</Label>
                <Select value={form.total_label || 'Total Due'} onValueChange={(v) => setForm({ ...form, total_label: v })}>
                  <SelectTrigger data-testid="total-label-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Total Due">Total Due</SelectItem>
                    <SelectItem value="Total">Total</SelectItem>
                    <SelectItem value="Grand Total">Grand Total</SelectItem>
                    <SelectItem value="Net Total">Net Total</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-emerald-700 mt-1">Shown on the highlighted total row in the printed invoice.</p>
              </div>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <div className="border-t pt-2 mt-2 text-sm space-y-1">
                <div className="flex justify-between"><span>Net amount</span><b>Rs. {fmtR(totals.netAmount)}</b></div>
                {enableDiscount && totals.discountTotal > 0 && (
                  <div className="flex justify-between text-slate-500"><span>Discount</span><b>Rs. {fmtR(totals.discountTotal)}</b></div>
                )}
                {totals.cgst > 0 && <div className="flex justify-between"><span>CGST</span><b>Rs. {fmtR(totals.cgst)}</b></div>}
                {totals.sgst > 0 && <div className="flex justify-between"><span>SGST</span><b>Rs. {fmtR(totals.sgst)}</b></div>}
                {totals.igst > 0 && <div className="flex justify-between"><span>IGST</span><b>Rs. {fmtR(totals.igst)}</b></div>}
                <div className="flex justify-between bg-emerald-100 -mx-3 px-3 py-1.5 font-bold text-emerald-900 mt-2"><span>{(form.total_label || 'Total Due').toUpperCase()} (INR)</span><span>Rs. {fmtR(totals.total)}</span></div>
                {totals.advance > 0 && (
                  <>
                    <div className="flex justify-between text-amber-700" data-testid="advance-row"><span>Less: Advance Paid</span><b>– Rs. {fmtR(totals.advance)}</b></div>
                    <div className="flex justify-between bg-amber-100 -mx-3 px-3 py-1.5 font-bold text-amber-900"><span>BALANCE DUE (INR)</span><span data-testid="balance-due">Rs. {fmtR(totals.balance)}</span></div>
                  </>
                )}
                <div className="text-[10px] italic text-emerald-800 -mx-3 px-3 pb-2 pt-1 bg-emerald-50/60 border-t border-emerald-200" data-testid="total-in-words">
                  In words: <b className="font-semibold">{numberToIndianWords(totals.advance > 0 ? totals.balance : totals.total)}</b>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditId(null); }}>Cancel</Button>
            <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800">{editId ? 'Update Invoice' : 'Create Invoice'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog (read-only PDF-style view) */}
      <Dialog open={!!previewInv} onOpenChange={(o) => !o && setPreviewInv(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          {previewInv && (<div dangerouslySetInnerHTML={{ __html: buildInvoiceHtml(previewInv, true) }} />)}
          <DialogFooter className="p-4">
            <Button variant="outline" onClick={() => setPreviewInv(null)}>Close</Button>
            <Button onClick={() => print(previewInv)} className="bg-emerald-700 hover:bg-emerald-800"><Printer className="w-4 h-4 mr-1" /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ----- HTML builder for print / preview — matches the user's sample PDF layout -----
function buildInvoiceHtml(inv, isPreview = false) {
  const t = calcTotals(inv);
  const hasDiscount = (inv.line_items || []).some(r => Number(r.discount_pct || 0) > 0);
  const items = t.items.map(r => `
    <tr>
      <td>${(r.description || '—').replace(/</g,'&lt;')}</td>
      <td class="r">${Number(r.price||0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      <td class="r">${r.qty}</td>
      ${hasDiscount ? `<td class="r">${Number(r.discount_pct||0)}%</td>` : ''}
      <td class="r">${Number(r.amount||0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
    </tr>`).join('');
  const fmt = (n) => Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `<!doctype html><html><head><title>Invoice ${inv.invoice_no}</title>
  <style>
  *{box-sizing:border-box} body{font-family:'Helvetica Neue',Arial,sans-serif;margin:0;padding:${isPreview ? '20px' : '14mm 12mm'};color:#0f172a;background:#fff;font-size:12px}
  .top{display:flex;justify-content:space-between;margin-bottom:30px;padding-bottom:20px;border-bottom:2px solid #0f172a}
  .top .left{font-size:11px;color:#64748b}
  .top .right{text-align:right}
  .top .right h1{font-size:28px;font-weight:800;letter-spacing:-.5px;margin:0 0 6px}
  .top .right .sender-name{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;line-height:1.4;margin-bottom:2px}
  .top .right .sender-addr{font-size:10px;color:#475569;line-height:1.5;max-width:280px;margin-left:auto}
  .parties{display:flex;justify-content:space-between;margin-bottom:25px;gap:30px}
  .party{flex:1}
  .party h3{font-size:12px;font-weight:700;color:#0f172a;text-transform:uppercase;margin:0 0 6px;letter-spacing:.5px}
  .party .name{font-size:13px;font-weight:700;margin-bottom:2px}
  .party .addr{font-size:11px;color:#475569;line-height:1.5;white-space:pre-line}
  .party .gst{font-size:11px;color:#0f172a;margin-top:6px;background:#f1f5f9;padding:3px 8px;border-radius:3px;display:inline-block;font-family:'SF Mono',Consolas,monospace}
  .meta{text-align:right;font-size:11px;line-height:1.8}
  .meta .lab{color:#64748b;display:inline-block;min-width:90px;text-align:left}
  .meta .val{font-weight:700;margin-left:8px}
  table.lines{width:100%;border-collapse:collapse;margin:8px 0 20px;font-size:11px}
  table.lines th{background:#0f172a;color:#fff;padding:8px 10px;text-align:left;text-transform:uppercase;font-weight:600;font-size:10px;letter-spacing:.3px}
  table.lines th.r,table.lines td.r{text-align:right}
  table.lines td{padding:9px 10px;border-bottom:1px solid #e2e8f0;vertical-align:top}
  .totals{display:flex;justify-content:flex-end;margin-top:6px}
  .totals table{font-size:11px;min-width:320px;border-collapse:collapse}
  .totals td{padding:4px 12px}
  .totals td:first-child{color:#475569}
  .totals td:last-child{text-align:right;font-weight:600}
  .totals tr.total-due td{background:#0f172a;color:#fff;font-size:14px;font-weight:700;padding:10px 12px;text-transform:uppercase;letter-spacing:.3px}
  .pay{margin-top:30px;padding:16px 18px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px}
  .pay h4{margin:0 0 8px;font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#0f172a}
  .pay .grid{display:grid;grid-template-columns:1fr 1fr;gap:6px 30px;font-size:11px}
  .pay .grid .lab{color:#64748b}
  .pay .grid .val{font-weight:600;font-family:'SF Mono',Consolas,monospace}
  .pay p.note{font-size:10px;color:#64748b;margin:0 0 8px;font-style:italic}
  .footer{margin-top:30px;padding-top:14px;border-top:1px solid #cbd5e1;display:flex;justify-content:space-between;font-size:10px;color:#64748b}
  .footer .org{font-weight:700;color:#0f172a;text-transform:uppercase;font-size:11px;letter-spacing:.3px}
  @media print{ @page{ size:A4 portrait; margin:0 } body{ -webkit-print-color-adjust:exact; print-color-adjust:exact } }
  </style></head><body>
  <div class="top">
    <div class="left">Page 1 of 1</div>
    <div class="right">
      <h1>INVOICE</h1>
      <div class="sender-name">${inv.sender_name || ''}</div>
      <div class="sender-addr">${(inv.sender_address || '').replace(/\n/g, '<br/>')}</div>
      ${inv.sender_gstin ? `<div class="gst" style="margin-top:6px;background:#0f172a;color:#fff;padding:3px 8px;border-radius:3px;font-family:'SF Mono',monospace;font-size:10px">GSTIN: ${inv.sender_gstin}</div>` : ''}
    </div>
  </div>

  <div class="parties">
    <div class="party">
      <h3>Bill to</h3>
      <div class="name">${inv.bill_to_name || ''}</div>
      <div class="addr">${(inv.bill_to_address || '').replace(/\n/g, '<br/>')}</div>
      ${inv.bill_to_gstin ? `<div class="gst">GSTIN: ${inv.bill_to_gstin}</div>` : ''}
    </div>
    <div class="party meta">
      <div><span class="lab">Invoice date:</span><span class="val">${fmtDate(inv.invoice_date)}</span></div>
      ${inv.due_date ? `<div><span class="lab">Due date:</span><span class="val">${fmtDate(inv.due_date)}</span></div>` : ''}
      <div><span class="lab">Invoice no:</span><span class="val">${inv.invoice_no}</span></div>
    </div>
  </div>

  <table class="lines">
    <thead><tr><th>Description</th><th class="r">Price</th><th class="r">Qty</th>${hasDiscount ? `<th class="r">Discount</th>` : ''}<th class="r">Amount</th></tr></thead>
    <tbody>${items}</tbody>
  </table>

  <div class="totals">
    <table>
      <tr><td>Net amount</td><td>${fmt(t.netAmount)}</td></tr>
      ${hasDiscount ? `<tr><td>Discount</td><td>${fmt(t.discountTotal)}</td></tr>` : ''}
      ${t.cgst > 0 ? `<tr><td>CGST (${(Number(inv.gst_rate||0)/2).toFixed(2)}%)</td><td>${fmt(t.cgst)}</td></tr>` : ''}
      ${t.sgst > 0 ? `<tr><td>SGST (${(Number(inv.gst_rate||0)/2).toFixed(2)}%)</td><td>${fmt(t.sgst)}</td></tr>` : ''}
      ${t.igst > 0 ? `<tr><td>IGST (${Number(inv.gst_rate||0).toFixed(2)}%)</td><td>${fmt(t.igst)}</td></tr>` : ''}
      <tr class="total-due"><td>${(inv.total_label || 'Total Due')} (INR)</td><td>${fmt(t.total)}</td></tr>
      ${t.advance > 0 ? `<tr><td>Less: Advance Paid</td><td>− ${fmt(t.advance)}</td></tr>` : ''}
      ${t.advance > 0 ? `<tr class="total-due"><td>Balance Due (INR)</td><td>${fmt(t.balance)}</td></tr>` : ''}
    </table>
  </div>
  <div class="amount-words">
    Amount in words: <b>${numberToIndianWords(t.advance > 0 ? t.balance : t.total)}</b>
  </div>

  <div class="pay">
    <h4>Payment information</h4>
    <p class="note">Please add the invoice number to the payment transfer.</p>
    <div class="grid">
      <div><span class="lab">Invoice number:</span></div><div class="val">${inv.invoice_no}</div>
      <div><span class="lab">Amount (INR):</span></div><div class="val">${fmt(t.advance > 0 ? t.balance : t.total)}</div>
      <div><span class="lab">Bank account name:</span></div><div class="val">${inv.bank_account_name || ''}</div>
      <div><span class="lab">Bank account number:</span></div><div class="val">${inv.bank_account_number || ''}</div>
      <div><span class="lab">Bank name:</span></div><div class="val">${inv.bank_name || ''}</div>
      <div><span class="lab">IFSC:</span></div><div class="val">${inv.ifsc || ''}</div>
    </div>
    ${inv.notes ? `<p style="margin-top:10px;font-size:11px;color:#475569"><b>Notes:</b> ${inv.notes}</p>` : ''}
  </div>

  <div class="footer">
    <span class="org">${inv.sender_name || ''}</span>
    <span>${inv.sender_website || ''} · ${inv.sender_email || ''}</span>
  </div>
  </body></html>`;
}

export default Invoices;
